import {
  Account,
  AccountRejectionReasons,
  Eligible_Estate_Account_Types,
  Eligible_Individual_Account_Types,
  Eligible_Joint_Account_Types,
  Eligible_Joint_Account_Types_Pattern,
  Eligible_Minor_Account_Types,
  Eligible_Roth_IRA_Account_Types,
  Eligible_Traditional_IRA_Account_Types,
  Eligible_Trust_Account_Types,
  FilteredAccount,
  FilteredClientAccount,
  IClientAccounts,
  IClientAccountsState,
  MarginCode,
  OptionPermission,
  Position,
  ProductType,
  RegisteredRole,
  RejectedAccount,
  RestrictionType,
  ServiceAgreement,
} from '@interfaces/models/client-accounts.interface';
import { MoneyMarketTickers } from '@interfaces/models/money-market-tickers-type';
import { PermissionType } from '@interfaces/models/permission-types';
import { InternalTransferFrom } from '@interfaces/state';
import { Action, createReducer, on } from '@ngrx/store';
import { getAccountName } from '@shared/helpers';
import {
  AccountTypes,
  Eligible_Estate_Role_Types,
  Eligible_Trust_Role_Types,
  RoleTypes,
  SecurityType,
} from '@shared/models';
import { CostBasisPosition } from '@store/reducers/cost-basis/cost-basis.reducer';
import { from } from 'rxjs';

import * as ClientAccountsActions from '../../actions/client-accounts/client-accounts.actions';
import { InternalTransferState } from '../internal-transfer/internal-transfer.reducer';

export * from '@interfaces/models/client-accounts.interface';
export const initialState: IClientAccountsState = {
  entity: null,
  displayedAccounts: [],
  loaded: false,
  loading: false,
};
type SpecificAccountTypes =
  | AccountTypes.JOINT
  | AccountTypes.INDV
  | AccountTypes.TRUST
  | AccountTypes.ESTATE
  | AccountTypes.UGMA_UTMA;
interface AccountTypeMapping {
  accountTypes: RegExp | Set<string>;
  verifyIsPrimaryOwner: boolean;
  excludesManaged: boolean;
  excludesMarginAndOptions: boolean;
  excludesNonBrokerage: boolean;
}

type AccountTypeMappings = {
  [key in SpecificAccountTypes]: AccountTypeMapping;
};

const accountTypeMapping: AccountTypeMappings = {
  [AccountTypes.JOINT]: {
    accountTypes: Eligible_Joint_Account_Types_Pattern,
    verifyIsPrimaryOwner: false,
    excludesManaged: true,
    excludesMarginAndOptions: true,
    excludesNonBrokerage: true,
  },
  [AccountTypes.INDV]: {
    accountTypes: Eligible_Individual_Account_Types,
    verifyIsPrimaryOwner: true,
    excludesManaged: true,
    excludesMarginAndOptions: true,
    excludesNonBrokerage: true,
  },
  [AccountTypes.TRUST]: {
    accountTypes: Eligible_Trust_Account_Types,
    verifyIsPrimaryOwner: false,
    excludesManaged: true,
    excludesMarginAndOptions: true,
    excludesNonBrokerage: true,
  },
  [AccountTypes.ESTATE]: {
    accountTypes: Eligible_Estate_Account_Types,
    verifyIsPrimaryOwner: false,
    excludesManaged: true,
    excludesMarginAndOptions: true,
    excludesNonBrokerage: true,
  },
  [AccountTypes.UGMA_UTMA]: {
    accountTypes: Eligible_Minor_Account_Types,
    verifyIsPrimaryOwner: false,
    excludesManaged: true,
    excludesMarginAndOptions: true,
    excludesNonBrokerage: true,
  },
};

const cashPlusTickers: Set<string> = new Set([
  MoneyMarketTickers.VMRXX,
  MoneyMarketTickers.VMSXX,
  MoneyMarketTickers.VYFXX,
  MoneyMarketTickers.VUSXX,
  MoneyMarketTickers.VCTXX,
]);

const clientAccountsReducer = createReducer(
  initialState,
  on(ClientAccountsActions.LoadClientAccounts, (state) => ({
    ...state,
    loading: true,
  })),
  on(ClientAccountsActions.LoadClientAccountsStop, (state) => ({
    ...state,
    loading: false,
  })),
  on(ClientAccountsActions.LoadClientAccountsFail, (state) => ({
    ...state,
    loaded: false,
    loading: false,
  })),
  on(ClientAccountsActions.LoadClientAccountsSuccess, (state, prop) => ({
    ...state,
    entity: prop.payload as IClientAccounts,
    loaded: true,
    loading: false,
  })),
  on(ClientAccountsActions.UpdateDisplayedAccounts, (state, prop) => ({
    ...state,
    displayedAccounts: prop.payload,
  })),
);

export function reducer(state: IClientAccountsState | undefined, action: Action) {
  return clientAccountsReducer(state, action);
}

export const getClientAccountsEntity = (state: IClientAccountsState) => state.entity;
export const getClientAccountsLoading = (state: IClientAccountsState) => state.loading;
export const getClientAccountsLoaded = (state: IClientAccountsState) => state.loaded;
export const getDisplayedAccounts = (state: IClientAccountsState) => state.displayedAccounts;

export function isClientPrimaryOwner(
  clientId: string | null,
  registeredRoles: RegisteredRole[] | null,
) {
  if (clientId && registeredRoles) {
    for (const role of registeredRoles) {
      if (clientId === role.poid.toString() && role.primaryOwner) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Find service agreement from array of service agreements.
 * @param serviceAgreementId
 * @param serviceAgreements
 * @param count < 0 to ignore this check or > 0 to verify that number of registered roles.
 */
export function getServiceAgreement(
  serviceAgreementId: string | null,
  serviceAgreements: ServiceAgreement[] | null,
) {
  if (!serviceAgreementId || !serviceAgreements) return null;

  return serviceAgreements.find((agreement) => {
    return agreement.serviceAgreementId.toString() === serviceAgreementId.toString();
  });
}

/**
 * @param account
 */
export function balanceCheck(account: Account, isTransferTo: boolean) {
  try {
    if (isTransferTo) {
      return true;
    }

    return Number(account.balance) > 0;
  } catch (e) {
    // Guard against bad account structure.
    return false;
  }
}

export function nonBrokerageCheck(account: Account, excludesNonBrokerage: boolean) {
  try {
    if (excludesNonBrokerage && account.productType !== ProductType.BROKERAGE) return false;
    return true;
  } catch (e) {
    // Guard against bad account structure.
    return false;
  }
}

export function nonBrokerageAndNonMutualFundCheck(
  account: Account,
  excludesNonBrokerageAndNonMutualFund: boolean,
) {
  try {
    if (
      excludesNonBrokerageAndNonMutualFund &&
      account.productType !== ProductType.BROKERAGE &&
      account.productType !== ProductType.MUTUAL_FUND
    )
      return false;
    return true;
  } catch (e) {
    // Guard against bad account structure.
    return false;
  }
}

export function managedCheck(account: Account, excludesManaged: boolean) {
  try {
    if (excludesManaged) return !account.managed;
    return true;
  } catch (e) {
    // Guard against bad account structure.
    return false;
  }
}

export function cashPlusCheck(account: Account, excludeCashPlus: boolean) {
  try {
    if (excludeCashPlus) return !account.cashManagementAccount;
    return true;
  } catch (e) {
    // Guard against bad account structure.
    return false;
  }
}

export function eligibilityRoleTypeCheck(
  serviceAgreements: ServiceAgreement[] | null,
  accountType: AccountTypes,
): boolean {
  if (!serviceAgreements) return false;

  let eligibleRoles: Set<RoleTypes>;
  switch (accountType) {
    case AccountTypes.ESTATE:
      eligibleRoles = Eligible_Estate_Role_Types;
      break;
    case AccountTypes.TRUST:
      eligibleRoles = Eligible_Trust_Role_Types;
      break;
    default:
      throw new Error(`Invalid account type: ${accountType}`);
  }

  return serviceAgreements.some((agreement) =>
    [agreement.registeredRoles, agreement.grantedRoles].some((roles) =>
      roles?.some((role) => isValueEligible(role.roleType, eligibleRoles)),
    ),
  );
}
export function eligibilityPermissionTypeCheck(serviceAgreements: ServiceAgreement[] | null) {
  if (!serviceAgreements) return false;
  return serviceAgreements.some((agreement) =>
    agreement.registeredRoles?.some((role) => role.permissionType === PermissionType.TRNS),
  );
}

function isValueEligible(value: string, rules: Set<string> | RegExp) {
  return rules instanceof Set ? rules.has(value) : value?.match(rules);
}

export function marginAndOptionsCheck(account: Account, excludesMarginAndOptions: boolean) {
  try {
    if (
      excludesMarginAndOptions &&
      (account.marginCode === MarginCode.MARGIN ||
        (account.optionPermission && account.optionPermission !== OptionPermission.NO_PAPERS))
    ) {
      return false;
    }
    return true;
  } catch (e) {
    // Guard against bad account structure.
    return false;
  }
}

// TODO: we'll definitely want to refactor this to have far fewer params. the exclusions object is a great way to do this
// because this function is only ever called by the getEligibleAccounts function, we can assume that rejectedAcccounts will always be provided either as an array or null
// eslint-disable-next-line max-params
export function validateAccountEligibility(
  accountTypes: Set<string> | RegExp,
  clientId: string,
  account: Account,
  serviceAgreements: ServiceAgreement[],
  isTransferTo: boolean,
  verifyIsPrimaryOwner: boolean,
  exclusions: {
    managed: boolean;
    marginAndOptions: boolean;
    nonBrokerage: boolean;
    nonBrokerageAndNonMutualFund: boolean;
    cashPlus: boolean;
  },
  rejectedAccounts: RejectedAccount[] | null,
): boolean {
  const rejectedAccountSeed: RejectedAccount = {
    accountId: account.accountId,
    accountName: account.accountName,
    accountType: account.accountType,
    formattedAccountName: getAccountName(
      {
        ...account,
        accountNumber: getAccountNumber(account),
        isBrokerage: account.productType === ProductType.BROKERAGE,
      },
      false,
    ),
    accountNumber: getAccountNumber(account),
  };
  if (account.nickname) {
    rejectedAccountSeed.nickname = account.nickname;
  }
  const pushRejectedAccount = (rejectionReason: AccountRejectionReasons) => {
    rejectedAccounts?.push({
      ...rejectedAccountSeed,
      rejectionReason,
    });
  };

  const accountTypeCheck = isValueEligible(account.accountType, accountTypes);
  const nonZeroBalanceCheck = balanceCheck(account, isTransferTo);
  const nonManagedAccountCheck = managedCheck(account, exclusions.managed);
  const nonCashPlusCheck = cashPlusCheck(account, exclusions.cashPlus);
  const nonMarginAndOptionsAccountCheck = marginAndOptionsCheck(
    account,
    exclusions.marginAndOptions,
  );
  const nonBrokerageAccountCheck = nonBrokerageCheck(account, exclusions.nonBrokerage);
  const nonBrokerageAndNonMutualAccountCheck = nonBrokerageAndNonMutualFundCheck(
    account,
    exclusions.nonBrokerageAndNonMutualFund,
  );
  let hasRoleTypeEligibility: boolean = true;
  let hasPermissionTypeEligibility: boolean = true;
  const isTrustAccount = isValueEligible(account.accountType, Eligible_Trust_Account_Types);
  const isEstateAccount = isValueEligible(account.accountType, Eligible_Estate_Account_Types);
  const isMinorAccount = isValueEligible(account.accountType, Eligible_Minor_Account_Types);

  if (isTrustAccount || isEstateAccount) {
    const accountType: AccountTypes = isTrustAccount ? AccountTypes.TRUST : AccountTypes.ESTATE;
    hasRoleTypeEligibility = eligibilityRoleTypeCheck(serviceAgreements, accountType);
  }
  if (isMinorAccount && isTransferTo) {
    hasPermissionTypeEligibility = eligibilityPermissionTypeCheck(serviceAgreements);
  }

  switch (true) {
    case !nonZeroBalanceCheck:
      pushRejectedAccount(AccountRejectionReasons.ZERO_BALANCE);
      return false;
    case !nonManagedAccountCheck:
      pushRejectedAccount(AccountRejectionReasons.MANAGED_ACCOUNT);
      return false;
    case !nonMarginAndOptionsAccountCheck:
      pushRejectedAccount(AccountRejectionReasons.MARGIN_OPTIONS_ACCOUNT);
      return false;
    case !nonBrokerageAccountCheck:
      pushRejectedAccount(AccountRejectionReasons.NON_BROKERAGE_ACCOUNT);
      return false;
    case !nonBrokerageAndNonMutualAccountCheck:
      pushRejectedAccount(AccountRejectionReasons.NON_BROKERAGE_OR_NON_MUTUAL_FUND_ACCOUNT);
      return false;
    case !nonCashPlusCheck:
      pushRejectedAccount(AccountRejectionReasons.CASH_PLUS_ACCOUNT);
      return false;
    case !accountTypeCheck:
      pushRejectedAccount(AccountRejectionReasons.ACCOUNT_TYPE);
      return false;
    case !hasRoleTypeEligibility:
      pushRejectedAccount(AccountRejectionReasons.NON_MATCHING_ROLE_TYPE);
      return false;
    case !hasPermissionTypeEligibility:
      pushRejectedAccount(AccountRejectionReasons.NON_MATCHING_PERMISSION_TYPE);
      return false;
  }

  const serviceAgreement = getServiceAgreement(account.serviceAgreementId, serviceAgreements);
  if (!serviceAgreement) {
    pushRejectedAccount(AccountRejectionReasons.INCORRECT_SERVICE_ACCOUNT_COUNT);
    return false;
  }
  if (verifyIsPrimaryOwner) {
    const primaryOwnerCheck = isClientPrimaryOwner(clientId, serviceAgreement.registeredRoles);
    if (!primaryOwnerCheck) {
      pushRejectedAccount(AccountRejectionReasons.NOT_PRIMARY_OWNER);
      return false;
    }
  }
  return true;
}

export function trimPositions(positions: Position[]) {
  const sortedPositions: Position[] = [];
  positions.forEach((position) => {
    const shareQuantity = +position.shareQuantity;
    if (position.settlementFund) {
      if (+position.balance > 0) {
        const securityName = position.securityName + ' (SETTLEMENT FUND)';
        const settlementFund = { ...position, securityName, shareQuantity };
        sortedPositions.unshift(settlementFund);
      }
    } else {
      sortedPositions.push({ ...position, shareQuantity });
    }
  });
  return sortedPositions;
}

export function getAccountNumber(account: Account) {
  let accountNumber = '0';
  if (account.brokerageAccountNumber) {
    accountNumber = account.brokerageAccountNumber;
  } else if (account.positions.length > 0 && account.positions[0].vastAccountNumber) {
    accountNumber = account.positions[0].vastAccountNumber;
  }
  return accountNumber;
}

export function createFilteredAccount(
  account: Account,
  agreement?: ServiceAgreement | null,
): FilteredAccount {
  const accountTemplate: FilteredAccount = {
    accountId: account.accountId,
    accountName: account.accountName,
    productType: account.productType,
    serviceAgreementId: account.serviceAgreementId,
    balance: account.balance,
    accountType: account.accountType,
    registrationType: account.registrationType,
    registrationSubType: account.registrationSubType,
    accountNumber: getAccountNumber(account),
    cashManagementAccount: account.cashManagementAccount || false,
    balanceAsOfDateTime: account.balanceAsOfDateTime,
    managed: account.managed,
    positions: trimPositions(account.positions),
    isBrokerage: !!account.brokerageAccountNumber,
    registeredRoles: agreement ? agreement.registeredRoles : [],
    grantedRoles: agreement ? agreement.grantedRoles : [],
    restrictions: account.restrictions,
  };
  if (account.nickname) {
    accountTemplate.nickname = account.nickname;
  }
  return accountTemplate;
}

/**
 * Filter logic should be consistent for all account types.
 * @param state
 * @param accountTypes
 * @param verifyIsPrimaryOwner
 * @param isTransferTo
 * @param excludesManaged
 * @param excludesMarginAndOptions
 * @param rejectedAccounts
 */
export const getEligibleAccounts = (
  state: IClientAccountsState,
  accountTypes: Set<string> | RegExp,
  verifyIsPrimaryOwner: boolean = true,
  isTransferTo: boolean = false,
  excludesManaged: boolean = false,
  excludesMarginAndOptions: boolean = false,
  excludesNonBrokerage: boolean = false,
  excludesNonBrokerageAndNonMutualFund: boolean = false,
  isCashPlusEnabled: boolean = false,
  rejectedAccounts: RejectedAccount[] | null = [],
) => {
  const entity = getClientAccountsEntity(state);
  const eligibleAccount: FilteredClientAccount = {
    vgClientPoid: null,
    accounts: [],
  };
  if (entity && entity.vgClientPoid && entity.accounts) {
    eligibleAccount.vgClientPoid = entity.vgClientPoid;
    entity.accounts.forEach((account) => {
      if (
        validateAccountEligibility(
          accountTypes,
          entity.vgClientPoid,
          account,
          entity.serviceAgreements,
          isTransferTo,
          verifyIsPrimaryOwner,
          {
            managed: excludesManaged,
            marginAndOptions: excludesMarginAndOptions,
            nonBrokerage: excludesNonBrokerage,
            nonBrokerageAndNonMutualFund: excludesNonBrokerageAndNonMutualFund,
            cashPlus: !isCashPlusEnabled,
          },
          rejectedAccounts,
        )
      ) {
        const filteredAccount: FilteredAccount = createFilteredAccount(
          account,
          getServiceAgreement(account.serviceAgreementId, entity.serviceAgreements),
        );

        if (filteredAccount) {
          eligibleAccount.accounts.push(filteredAccount);
        }
      }
    });
  }
  return eligibleAccount;
};

export const getEligibleClientAccount = (
  isIraFlow: boolean,
  iraType: string | null,
  state: IClientAccountsState,
  isTransferTo: boolean,
): FilteredClientAccount => {
  if (isIraFlow) {
    if (iraType === 'ROTH') {
      return getEligibleRothIRAAccounts(state, isTransferTo);
    } else {
      return getEligibleTraditionalIRAAccounts(state, isTransferTo);
    }
  } else {
    return getEligibleIndividualAccounts(state, isTransferTo);
  }
};

export const getEligibleJointAccounts = (
  state: IClientAccountsState,
  isTransferTo: boolean,
  showManagedAccounts = false,
): FilteredClientAccount => {
  return getEligibleAccounts(
    state,
    Eligible_Joint_Account_Types_Pattern,
    false,
    isTransferTo,
    !showManagedAccounts,
  );
};

export const getEligibleWROSJointAccounts = (
  state: IClientAccountsState,
  isTransferTo: boolean,
) => {
  return getEligibleAccounts(state, Eligible_Joint_Account_Types, false, isTransferTo);
};

export const getEligibleMinorAccounts = (state: IClientAccountsState) => {
  return getEligibleAccounts(state, Eligible_Minor_Account_Types);
};

export const getEligibleIndividualAccounts = (
  state: IClientAccountsState,
  isTransferTo: boolean,
) => {
  return getEligibleAccounts(state, Eligible_Individual_Account_Types, true, isTransferTo);
};

export const getEligibleTraditionalIRAAccounts = (
  state: IClientAccountsState,
  isTransferTo: boolean,
) => {
  return getEligibleAccounts(state, Eligible_Traditional_IRA_Account_Types, true, isTransferTo);
};

export const getEligibleRothIRAAccounts = (state: IClientAccountsState, isTransferTo: boolean) => {
  return getEligibleAccounts(state, Eligible_Roth_IRA_Account_Types, true, isTransferTo);
};

export const getEligibleIndividualGiftingAccounts = (
  state: IClientAccountsState,
  showManagedAccounts = false,
) => {
  return getEligibleAccounts(
    state,
    Eligible_Individual_Account_Types,
    true,
    false,
    !showManagedAccounts,
  );
};

export const getEligibleTrustGiftingAccounts = (
  state: IClientAccountsState,
  showManagedAccounts = false,
) => {
  return getEligibleAccounts(
    state,
    Eligible_Trust_Account_Types,
    false,
    false,
    !showManagedAccounts,
  );
};

export const isSpecificAccountType = (
  accountType: AccountTypes | null,
): accountType is SpecificAccountTypes =>
  !!accountType &&
  [
    AccountTypes.JOINT,
    AccountTypes.INDV,
    AccountTypes.TRUST,
    AccountTypes.ESTATE,
    AccountTypes.UGMA_UTMA,
  ].includes(accountType);

export const areAllPositionsMoneyMarketFunds = (selectPositions: CostBasisPosition[]): boolean => {
  return selectPositions.every(
    (position) => position.ticker !== null && position.ticker in MoneyMarketTickers,
  );
};

export const fromPositionsHasMoneyMarketFund = (
  internalTransferFrom: InternalTransferFrom | null,
): boolean => {
  const fromPositions = internalTransferFrom?.selectedAccount?.accountData?.positions || [];
  return fromPositions.some(
    (position) => !!position.ticker && position.ticker in MoneyMarketTickers,
  );
};

export const areAllPositionsMutualFunds = (selectPositions: CostBasisPosition[]): boolean => {
  if (!selectPositions?.length) return false;
  return selectPositions.every((position) => position.securityType === SecurityType.MUTUAL_FUND);
};

export const getEligibleInternalTransferFromAccounts = (
  state: IClientAccountsState,
  accountType: SpecificAccountTypes | null,
  isCashPlusEnabled: boolean,
  rejectedAccounts: RejectedAccount[] = [],
): FilteredClientAccount => {
  if (!accountType) {
    const account: FilteredClientAccount = {
      vgClientPoid: null,
      accounts: [],
    };
    return account;
  }

  const mapping = accountTypeMapping[accountType];

  const filterAccounts: FilteredClientAccount = getEligibleAccounts(
    state,
    mapping.accountTypes,
    mapping.verifyIsPrimaryOwner,
    false,
    mapping.excludesManaged,
    mapping.excludesMarginAndOptions,
    false,
    true,
    isCashPlusEnabled,
    rejectedAccounts,
  );

  filterAccounts.accounts = filterAccounts.accounts.filter((account) => {
    if (account.cashManagementAccount) {
      // filter out settlement funds
      const filteredPositions: Position[] = account.positions.filter(
        (position) => !position.settlementFund,
      );

      // check if there is at least one money market fund
      const hasMoneyMarketFund: boolean = filteredPositions.some((position) =>
        cashPlusTickers.has(position.ticker),
      );

      if (hasMoneyMarketFund) {
        // Update account positions and include the account
        account.positions = filteredPositions;
        return true;
      } else {
        // exclude the account and push to rejectedAccounts
        rejectedAccounts.push({
          accountId: account.accountId,
          accountName: account.accountName,
          accountType: account.accountType,
          formattedAccountName: getAccountName(
            {
              ...account,
              accountNumber: getAccountNumber({
                ...account,
                optionPermission: OptionPermission.NO_PAPERS,
                marginCode: MarginCode.CASH,
              }),
            },
            false,
          ),
          accountNumber: getAccountNumber({
            ...account,
            optionPermission: OptionPermission.NO_PAPERS,
            marginCode: MarginCode.CASH,
          }),
          rejectionReason: AccountRejectionReasons.ZERO_BALANCE,
        });
        return false;
      }
    }
    //include non-cash management accounts without changes
    return true;
  });
  return filterAccounts;
};

export const getEligibleInternalTransferToAccounts = (
  state: IClientAccountsState,
  accountType: SpecificAccountTypes | null,
  internalTransferFromState: InternalTransferState['transferFrom'] | null,
  isCashPlusEnabled: boolean,
  isToFromConsolidationEnabled: boolean,
  selectPositions: CostBasisPosition[],
  rejectedAccounts: RejectedAccount[] | null = [],
) => {
  if (!accountType) {
    return { accounts: [] };
  }

  const mapping: AccountTypeMapping = accountTypeMapping[accountType];
  const eligibleToAccounts: FilteredClientAccount = getEligibleAccounts(
    state,
    mapping.accountTypes,
    mapping.verifyIsPrimaryOwner,
    true,
    mapping.excludesManaged,
    mapping.excludesMarginAndOptions,
    mapping.excludesNonBrokerage,
    false,
    isCashPlusEnabled,
    rejectedAccounts,
  );
  eligibleToAccounts.accounts = eligibleToAccounts.accounts.filter((accounts) => {
    return accounts.accountId !== internalTransferFromState?.selectedAccount?.accountData.accountId;
  });

  if (
    isCashPlusEnabled &&
    ((!isToFromConsolidationEnabled && !areAllPositionsMoneyMarketFunds(selectPositions)) ||
      !fromPositionsHasMoneyMarketFund(internalTransferFromState))
  ) {
    eligibleToAccounts.accounts = eligibleToAccounts.accounts.filter((account) => {
      if (account.cashManagementAccount) {
        rejectedAccounts?.push({
          accountId: account.accountId,
          accountName: account.accountName,
          accountType: account.accountType,
          formattedAccountName: getAccountName(
            {
              ...account,
              accountNumber: getAccountNumber({
                ...account,
                optionPermission: OptionPermission.NO_PAPERS,
                marginCode: MarginCode.CASH,
              }),
            },
            false,
          ),
          accountNumber: getAccountNumber({
            ...account,
            optionPermission: OptionPermission.NO_PAPERS,
            marginCode: MarginCode.CASH,
          }),
          rejectionReason: AccountRejectionReasons.NON_MONEY_MARKET_POSITION_SELECTED,
        });
      }
      return !account.cashManagementAccount;
    });
  }

  // Filter out accounts that have ACTR restriction if the transfer includes non-money market funds
  if (!areAllPositionsMutualFunds(selectPositions)) {
    eligibleToAccounts.accounts = eligibleToAccounts.accounts.filter((account) => {
      const hasRestriction = account.restrictions.includes(RestrictionType.ACTR);
      if (hasRestriction) {
        rejectedAccounts?.push({
          ...account,
          formattedAccountName: getAccountName(account, false),
          rejectionReason: AccountRejectionReasons.ACTR_RESTRICTION,
        });
      }
      return !hasRestriction;
    });
  }

  return eligibleToAccounts;
};
